import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, categoryArticles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'dianying');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const articles = categoryArticles[activeSlug] || [];

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  return (
    <>
      <SEOMeta
        title={`${currentCategory.name} - 91麻豆高清视频`}
        description={currentCategory.description}
        keywords={`91麻豆,${currentCategory.name},91麻豆高清视频,91麻豆${currentCategory.name}资源`}
        canonicalUrl={`https://clinicvip.cn/category/${activeSlug}`}
      />

      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500">{currentCategory.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-foreground-600 hover:bg-background-100 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                <span>{cat.name}</span>
                <span className={`text-xs ${activeSlug === cat.slug ? 'text-white/70' : 'text-foreground-400'}`}>
                  {cat.count.toLocaleString()}
                </span>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-12">
            {articles.map((article) => (
              <article key={article.id} className="bg-white rounded-lg overflow-hidden group cursor-pointer">
                <Link to={`/article/${article.id}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                </Link>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-foreground-400">{article.date}</span>
                    <span className="text-xs text-foreground-400">·</span>
                    <span className="text-xs text-foreground-400">{article.author}</span>
                  </div>
                  <Link to={`/article/${article.id}`}>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                  </Link>
                  <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs rounded-full bg-background-100 text-foreground-500 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 mt-3 text-xs text-foreground-400">
                    <i className="ri-eye-line"></i>
                    <span>{article.views.toLocaleString()} 次浏览</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}